import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCustomer } from "../context/CustomerContext";

/**
 * Global customer switcher for the app header. Click to open a searchable
 * list; pick "All customers" or a specific customer. The choice scopes every
 * customer-centric page (M365, AI Actions, …) via CustomerContext.
 */
export default function CustomerSwitcher() {
  const { currentCustomerId, current, customers, setCurrentCustomer } = useCustomer();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => c.name.toLowerCase().includes(q));
  }, [customers, query]);

  const label = current ? current.name : "All customers";

  const pick = (id: number | null) => {
    setCurrentCustomer(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700 max-w-[16rem]"
        title="Switch customer"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <svg className="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" />
        </svg>
        <span className="truncate">
          <span className="text-gray-400 dark:text-slate-500">Customer:</span>{" "}
          <span className="font-medium">{label}</span>
        </span>
        <svg className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-lg dark:bg-slate-800 dark:border-slate-700">
          <div className="p-2 border-b border-gray-100 dark:border-slate-700">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search customers…"
              className="w-full border rounded-lg px-2.5 py-1.5 text-sm dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto py-1 text-sm" role="listbox">
            <li>
              <button
                onClick={() => pick(null)}
                className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-700 ${
                  currentCustomerId == null ? "font-semibold text-blue-600 dark:text-blue-400" : "text-gray-700 dark:text-slate-200"
                }`}
              >
                All customers
              </button>
            </li>
            {filtered.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => pick(c.id)}
                  className={`w-full text-left px-3 py-1.5 truncate hover:bg-gray-50 dark:hover:bg-slate-700 ${
                    currentCustomerId === c.id ? "font-semibold text-blue-600 dark:text-blue-400" : "text-gray-700 dark:text-slate-200"
                  }`}
                >
                  {c.name}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-gray-400 dark:text-slate-500 italic">No customers match.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
